import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Search,
  Calendar,
  Upload,
  Trophy,
  Clock,
  Coins,
  ChevronRight,
  Loader2,
} from "lucide-react";
import Wallet3 from "../../../../Images/LE_coin.png";
import { useNavigate } from "react-router-dom";
import { getContestHistory } from "../../../../services/contestService";

const ContestHistory = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contestData, setContestData] = useState([]);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    wins: 0,
    avgScore: 0,
    streak: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getContestHistory();
        if (res?.success) {
          setContestData(res.result?.history || []);
          setStats(
            res.result?.stats || {
              totalEarnings: 0,
              wins: 0,
              avgScore: 0,
              streak: 0,
            },
          );
        } else {
          setError(res?.message || "Failed to load contest history.");
        }
      } catch (err) {
        console.error(err);
        setError("An error occurred while fetching your history.");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const filteredData =
    activeTab === "All"
      ? contestData
      : contestData.filter((item) => item.status === activeTab);

  return (
    <div className="w-full text-white animate-fade-in-up">
      {/* Stats Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Total Earnings Card */}
        <div className="bg-[#3F234E] rounded-3xl p-6 relative overflow-hidden shadow-lg border border-white/5 flex items-center justify-between col-span-1 lg:col-span-2">
          <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
          <div className="flex flex-col z-10">
            <p className="text-gray-300 text-sm font-medium mb-1">
              Total Contest Earnings
            </p>
            <div className="flex items-baseline gap-2">
              <h2 className="text-4xl font-bold text-white">
                {stats.totalEarnings.toLocaleString()}
              </h2>
              <span className="text-orange-400 text-sm font-semibold bg-orange-500/10 px-2 py-0.5 rounded-full">
                ● Coins
              </span>
            </div>
          </div>
          <img
            src={Wallet3}
            alt="wallet"
            className="w-24 h-24 object-contain drop-shadow-lg hidden sm:block opacity-90"
          />
        </div>

        {/* Stats Grid */}
        <div className="bg-[#3F234E] rounded-3xl p-6 border border-white/5 flex flex-col justify-center">
          <p className="text-gray-300 text-sm font-medium mb-4">
            Performance Overview
          </p>
          <div className="flex justify-between items-center text-center">
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold text-white">
                {stats.wins}
              </span>
              <span className="text-xs text-gray-400 uppercase tracking-wider mt-1">
                Wins
              </span>
            </div>
            <div className="w-px h-10 bg-white/10"></div>
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold text-white">
                {stats.avgScore}
              </span>
              <span className="text-xs text-gray-400 uppercase tracking-wider mt-1">
                Avg Score
              </span>
            </div>
            <div className="w-px h-10 bg-white/10"></div>
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold text-white">
                {stats.streak}
              </span>
              <span className="text-xs text-gray-400 uppercase tracking-wider mt-1">
                Streak
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar (Desktop) or Top Bar (Mobile) */}
        <div className="lg:col-span-1">
          <div className="bg-[#3F234E] rounded-3xl p-4 border border-white/5 sticky top-4">
            <h4 className="text-gray-400 font-bold mb-4 px-2 hidden lg:block">
              Filter History
            </h4>
            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 custom-scrollbar">
              {["All", "Won", "Lost", "Pending"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all text-left flex items-center justify-between ${
                    activeTab === tab
                      ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md"
                      : "text-gray-400 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  {tab}
                  {activeTab === tab && (
                    <ChevronRight size={16} className="hidden lg:block" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* History List */}
        <div className="lg:col-span-3 space-y-4">
          {/* Search Bar */}
          <div className="flex gap-4 mb-2">
            <div className="flex-1 bg-[#3F234E] rounded-2xl px-4 py-3 flex items-center gap-3 border border-white/5 focus-within:border-purple-500 transition-colors">
              <Search size={18} className="text-gray-400" />
              <input
                type="text"
                placeholder="Search contests..."
                className="bg-transparent border-none outline-none text-white w-full placeholder-gray-500"
              />
            </div>
            <div className="bg-[#3F234E] rounded-2xl px-4 py-3 flex items-center gap-2 border border-white/5 text-gray-300 font-medium whitespace-nowrap">
              <Calendar size={18} />
              <span className="hidden sm:inline">Recent</span>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-[#3F234E] rounded-3xl border border-white/5 border-dashed">
              <Loader2
                className="animate-spin text-purple-500 mb-4"
                size={48}
              />
              <p className="text-gray-400 text-lg">Loading history...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20 bg-[#3F234E] rounded-3xl border border-white/5 border-dashed">
              <p className="text-red-400 text-lg">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-medium transition"
              >
                Retry
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-20 bg-[#3F234E] rounded-3xl border border-white/5 border-dashed">
              <Trophy size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg">
                No contests found in this category.
              </p>
            </div>
          ) : (
            filteredData.map((item) => (
              <div
                key={item.id}
                className="bg-[#3F234E] rounded-2xl p-5 border border-white/5 hover:border-purple-500/50 transition-all duration-300 group cursor-pointer"
                onClick={() =>
                  navigate("/dash/contest-winner", {
                    state: { contestId: item.contestId },
                  })
                }
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-bold text-lg text-white group-hover:text-purple-300 transition-colors">
                      {item.title}
                    </h4>
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                      <Clock size={12} /> {item.date}
                    </p>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
                                ${
                                  item.status === "Won"
                                    ? "bg-green-500/20 text-green-400"
                                    : item.status === "Lost"
                                      ? "bg-red-500/20 text-red-400"
                                      : "bg-yellow-500/20 text-yellow-400"
                                }`}
                  >
                    {item.status}
                  </div>
                </div>

                <div className="w-full h-px bg-white/5 my-3"></div>

                {item.status === "Won" && (
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Prize</p>
                      <p className="font-bold text-green-400">{item.prize}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Rank</p>
                      <p className="font-bold text-white">
                        {item.rank || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Entry</p>
                      <p className="font-bold text-gray-300">{item.entry}</p>
                    </div>
                  </div>
                )}

                {item.status === "Lost" && (
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Rank</p>
                      <p className="font-bold text-white">
                        {item.rank || "N/A"}
                      </p>
                    </div>
                    {item.coins && item.coins !== "0 Coins" && (
                      <div>
                        <p className="text-gray-500 text-xs mb-1">
                          Coins Earned
                        </p>
                        <p className="font-bold text-green-400">{item.coins}</p>
                      </div>
                    )}
                  </div>
                )}
                {item.status === "Pending" && (
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Status</p>
                      <p className="font-bold text-yellow-400">
                        Results Pending
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ContestHistory;
